import {
  InfiniteListBase,
  ShowBase,
  useShowContext,
  useTranslate,
} from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { NoteCreate } from "../notes/NoteCreate";
import { NotesIterator } from "../notes/NotesIterator";
import type { Partner } from "../types";
import { PartnerAside, PartnerDetails } from "./PartnerAside";
import { PartnerKind } from "./PartnerKind";
import { PartnerDeals } from "./PartnerDeals";
import { PartnerEvents } from "./PartnerEvents";
import { PartnerEmailsTab } from "../mail/RecordEmailWidgets";

export const PartnerShow = () => {
  const isMobile = useIsMobile();
  return (
    <ShowBase>
      {isMobile ? <PartnerShowContentMobile /> : <PartnerShowContent />}
    </ShowBase>
  );
};

const PartnerShowContent = () => {
  const { record, isPending } = useShowContext<Partner>();
  if (isPending || !record) return null;

  return (
    <div className="mt-2 flex pb-2 gap-8">
      <div className="flex-1 min-w-0">
        <Card>
          <CardContent>
            <PartnerTitle record={record} />
            <PartnerTabs record={record} />
          </CardContent>
        </Card>
      </div>
      <PartnerAside record={record} />
    </div>
  );
};

const PartnerShowContentMobile = () => {
  const translate = useTranslate();
  const { record, isPending } = useShowContext<Partner>();
  if (isPending || !record) return null;

  return (
    <>
      <MobileHeader>
        <MobileBackButton />
        <h1 className="flex-1 text-xl font-semibold">
          {translate("resources.partners.name", { smart_count: 2 })}
        </h1>
      </MobileHeader>
      <MobileContent>
        <PartnerTitle record={record} />
        <PartnerDetails record={record} />
        <PartnerTabs record={record} />
      </MobileContent>
    </>
  );
};

const PartnerTitle = ({ record }: { record: Partner }) => (
  <div className="mb-4 flex flex-col gap-1">
    <h2 className="text-2xl font-semibold">{record.name}</h2>
    <PartnerKind partner={record} />
  </div>
);

/** Activity (notes), the opportunities the partner works on and their events. */
const PartnerTabs = ({ record }: { record: Partner }) => {
  const translate = useTranslate();
  return (
    <Tabs defaultValue="activity">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="activity">
          {translate("crm.common.activity")}
        </TabsTrigger>
        <TabsTrigger value="deals">
          {translate("resources.deals.name", { smart_count: 2 })}
        </TabsTrigger>
        <TabsTrigger value="events">
          {translate("resources.events.name", { smart_count: 2 })}
        </TabsTrigger>
        <TabsTrigger value="emails">
          {translate("crm.mail.emails.tab")}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="activity" className="pt-2">
        <InfiniteListBase
          resource="partner_notes"
          filter={{ partner_id: record.id }}
          sort={{ field: "date", order: "DESC" }}
          perPage={25}
          disableSyncWithLocation
          storeKey={false}
          empty={<NoteCreate reference="partners" className="mt-4" />}
        >
          <NotesIterator reference="partners" />
        </InfiniteListBase>
      </TabsContent>
      <TabsContent value="deals">
        <PartnerDeals partnerId={record.id} />
      </TabsContent>
      <TabsContent value="events">
        <PartnerEvents partnerId={record.id} />
      </TabsContent>
      <TabsContent value="emails">
        <PartnerEmailsTab partner={record} />
      </TabsContent>
    </Tabs>
  );
};
